import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { fetchSelfCheckinPrint, printerLog } from '@/apis/aipass'
import dayjs from 'dayjs'
import html2canvas from 'html2canvas'
import QRCode from 'qrcode.react'
import uuid from 'react-uuid'
import { DeviceType } from '@/apis/vescajs'
import { PrinterContextCreator } from '@/contexts/printer'
import { errorHandler, toErrorMessage } from '@/libs/errors'
import { usePickByLanguage } from '@/hooks/use-pick-by-language'
import { CASH, CREDIT_CARD, CASH_CHANGER } from '@/models/payment-method'
import { isAllSameStayPeriod, StayPeriods } from '@/utils/stay-period'
import { checkinEposPrinterIdStorage } from '@/constants/self-checkin-device-storage'
import { SelfCheckinCompleteState } from '@/components/pages/self-checkin/complete'
import { TerminalCreditSalesSlip } from '@/components/molecules/front-tablet/receipt/terminal-credit-sales-slip'
import { QrCodeSalesSlip } from '@/components/molecules/front-tablet/receipt/qr-code-sales-slip'
import { reportError } from '@/components/apps/error-boundary'
import { PrinterUnavailableNotice } from './printer-unavailable-notice'

type Props = {
  state: SelfCheckinCompleteState
  onComplete: () => void
  isIssueRoomKey?: boolean
}

type TranslationStringType = {
  ja: string
  en: string
  zh: string
  ko: string
}

interface CheckinPrint {
  guestAppUrl: string
  guestName: string
  reservations: Array<{
    reservationId: string
    guestName: string
    paxOfAdults: number
    paxOfChildren: number
    checkinAt: string
    checkoutAt: string
    plan: TranslationStringType
    rooms: Array<{
      number: string
      key: string
      checkinDate?: string | null
      checkoutDate?: string | null
      qrCode?: string | null
      isCardReplacementRequired: boolean
    }>
    timeSelections: Array<{
      name: TranslationStringType
      selected: string[]
    }>
  }>
  hotel: {
    name: TranslationStringType
    postalCode: string | undefined
    address: TranslationStringType
    comment: TranslationStringType
    receiptRegistrationNumber: string
    receiptLogoImage: string | null
    receiptTaxOfficeName: string | null
  }
  spaces: Array<{
    [reservationId: string]: Array<{ title: TranslationStringType; from: string }[]>
  }>
}

export const CheckinReceipt: React.FC<Props> = props => {
  const { t } = useTranslation()
  const pickByLanguage = usePickByLanguage()
  const { printImage, disconnect } = useContext(PrinterContextCreator())
  const [printCheckin, setPrintCheckin] = useState<CheckinPrint>()
  const printContentRef = useRef<HTMLDivElement | null>(null)

  // 用紙サイズ 80mm=576px
  const paperSize = 576

  const isNeedReceipt =
    props.state.payment && [CREDIT_CARD, CASH_CHANGER].includes(props.state.paymentMethod || '') && props.state.payment.paymentAmount > 0

  const printCheckInReceipt = async () => {
    try {
      setPrintCheckin(
        await fetchSelfCheckinPrint({
          hotelId: props.state.hotelId,
          checkinId: props.state.checkinId,
          reservationIdWithAssignIds: props.state.selectedReservationAndRooms,
        }),
      )
    } catch (e) {
      reportError(e)
      alert(t('SelfCheckIn.Failed to print'))
    }
  }

  useEffect(() => {
    printCheckInReceipt()
  }, [])

  const hasUnpaid: boolean = useMemo(() => {
    return props.state.paymentMethod === CASH && props.state.confirmUsageTotalAmount > 0
  }, [])

  const receiptProviso = useMemo(() => {
    if (!isNeedReceipt) {
      return ''
    }
    let methodName = t('Credit card')
    if (props.state.payment?.deviceType === DeviceType.PaidQr) {
      methodName = props.state.payment.brandName
    } else if (props.state.paymentMethod === CASH_CHANGER) {
      methodName = t('Cash')
    }
    return t('SelfCheckIn.Received by {{n}} for accommodation.', { n: methodName })
  }, [props])

  // QRコードがある部屋が1部屋でもあるかどうか（printCheckin取得後に評価）
  const hasQrCode = useMemo(() => {
    if (!printCheckin) return false
    return printCheckin.reservations.flatMap(r => r.rooms).some(room => !!room.qrCode)
  }, [printCheckin])

  // 全ての予約の全ての部屋が同じ宿泊期間かどうかを判定
  const isAllSameStayPeriods = useMemo(() => {
    if (!printCheckin) return false

    const allRooms = printCheckin.reservations.flatMap(reservation => reservation.rooms)
    if (allRooms.length === 0) return false

    const stayPeriods: StayPeriods = allRooms
      .filter(room => room.checkinDate && room.checkoutDate)
      .map(room => ({
        roomCheckinDate: room.checkinDate || '',
        roomCheckoutDate: room.checkoutDate || '',
      }))

    return stayPeriods.length > 0 && isAllSameStayPeriod(stayPeriods)
  }, [printCheckin])

  const doPrint = async (divNode: HTMLDivElement): Promise<boolean> => {
    try {
      const receiptImageCanvas = await html2canvas(divNode, {
        scale: 1,
        onclone: doc => {
          const div = doc.getElementById('print-content')
          div!.style.display = 'block'
        },
      })
      await printImage({
        canvas: receiptImageCanvas,
        filename: props.state.checkinId,
        hotelId: props.state.hotelId,
        connectIdKey: checkinEposPrinterIdStorage,
        isStayConnected: true,
        reservationUuid: props.state.uploadReservationUuid,
      })
      printerLog({ step: 'success', state: props.state })
      props.onComplete()
      return true
    } catch (e) {
      reportError(e)
      errorHandler(e)
      printerLog({ step: 'failed', message: toErrorMessage(e), state: props.state })
      return false
    }
  }

  const receiptDom = useMemo(() => {
    return (divNode: HTMLDivElement | null) => {
      printerLog({ step: 'dom load' })
      if (divNode === null) {
        printerLog({ step: 'dom unload' })
        return
      }
      printContentRef.current = divNode
      doPrint(divNode)
    }
  }, [])

  const handleReprint = async () => {
    if (!printContentRef.current) {
      return
    }
    // 応答しない接続を掴んだまま再試行しても同じ結果になるため、切断してから印刷し直す（接続は doPrint 側で張り直される）
    disconnect()
    const isPrinted = await doPrint(printContentRef.current)
    printerLog({ message: isPrinted ? 'connect' : 'error retry connection.', mode: 'retry' })
  }

  if (!printCheckin) {
    return <></>
  }
  return (
    <div>
      <PrinterUnavailableNotice onClick={handleReprint} />
      <div id="print-content" ref={receiptDom} css={[{ width: paperSize }, receiptStyle]}>
        {printCheckin.hotel.receiptLogoImage && (
          <div className="logo-block">
            <img src={printCheckin.hotel.receiptLogoImage} alt="receipt logo" />
          </div>
        )}
        <div className="accommodation-block">
          <h2>{t('SelfCheckIn.Accommodation')}</h2>
          <hr className="-normal" />
          {printCheckin.reservations.map(reservation => (
            <div key={uuid()}>
              <div>
                {reservation.rooms.map((room, i) => {
                  const qrCode = room.qrCode
                  return (
                    <div className="room-block" key={i}>
                      {/* 客室の期間がひとつでも異なる場合は、宿泊期間を表示 */}
                      {!isAllSameStayPeriods && (
                        <div className="stay-period-item">
                          <span className="label">{t('SelfCheckIn.Guest room period')}</span>
                          <span className="value stay-period-value">
                            {room.checkinDate && room.checkoutDate
                              ? dayjs(room.checkinDate).format('MM/DD') + ' ~ ' + dayjs(room.checkoutDate).format('MM/DD')
                              : '-'}
                          </span>
                        </div>
                      )}
                      {qrCode ? (
                        <div className="qr-code-container">
                          <div className="qr-code-room-number">
                            <span className="label">{t('SelfCheckIn.Room')}</span>
                            <span className="value">{room.number}</span>
                          </div>
                          <div className="qr-code-block">
                            <QRCode value={qrCode} size={160} />
                          </div>
                          {room.key && (
                            <div className="key-number-block">
                              {t('SelfCheckIn.Key')} : {room.key}
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          <div>
                            <span className="label">{t('SelfCheckIn.Room')}</span>
                            <span className="value">{room.number}</span>
                          </div>
                          {room.key && (
                            <div>
                              <span className="label">{t('SelfCheckIn.Key')}</span>
                              <span className="value">{room.key}</span>
                            </div>
                          )}
                          {props.isIssueRoomKey && room.checkinDate && room.isCardReplacementRequired && (
                            <div>
                              <span style={{ fontSize: 24 }}>
                                {t('SelfCheckIn.cardReplacementNotice', {
                                  date: dayjs(room.checkinDate).format('MM/DD'),
                                  interpolation: { escapeValue: false },
                                })}
                              </span>
                            </div>
                          )}
                        </>
                      )}
                      {reservation.rooms.length !== i + 1 ? (
                        <hr className="-block" />
                      ) : (
                        <>
                          {hasQrCode && (
                            <div className="qrcode-description">
                              {t('SelfCheckIn.QRCodeDoorInstruction')}
                              <br /> {t('SelfCheckIn.QRCodeReceiptAdvice')}
                            </div>
                          )}
                          <hr className="-normal" />
                        </>
                      )}
                    </div>
                  )
                })}
              </div>
              <div className="reservation-block">
                {!props.state.hasAnyGuestCheckedIn && (
                  <>
                    <div className="list-item">
                      <span className="label">{t('Your name')}</span>
                      <span className="value">
                        {reservation.guestName} {t('SelfCheckIn.To')}
                      </span>
                    </div>
                    <div className="list-item">
                      <span className="label">{t('SelfCheckIn.No. of guests')}</span>
                      <span className="value">
                        {t('Adult')}：{reservation.paxOfAdults}、{t('Child')}:{reservation.paxOfChildren}
                      </span>
                    </div>
                    <div className="list-item">
                      <span className="label">{t('SelfCheckIn.Period of stay')}</span>
                      <span className="value">
                        {dayjs(reservation.checkinAt).format('MM/DD')} ~ {dayjs(reservation.checkoutAt).format('MM/DD')}
                      </span>
                    </div>
                    {reservation.plan.ja && (
                      <div className="list-item">
                        <span className="label">{t('Plan name')}</span>
                        <span className="value">{pickByLanguage(reservation.plan)}</span>
                      </div>
                    )}
                  </>
                )}
                {printCheckin.spaces[reservation.reservationId]?.map(ts => (
                  <div className="list-item" key={uuid()}>
                    <span className="label">{pickByLanguage(ts[0].title)}</span>
                    <span className="value">
                      {ts.map(({ from }) => (
                        <React.Fragment key={uuid()}>
                          {dayjs(from).format('MM/DD(ddd) HH:mm')}
                          <br />
                        </React.Fragment>
                      ))}
                    </span>
                  </div>
                ))}
              </div>
              <hr className="-normal" />
            </div>
          ))}

          <div className="comment">{pickByLanguage(printCheckin.hotel.comment)}</div>
        </div>

        <hr className="-block" />

        {isNeedReceipt && (
          <>
            <div className="payment-card-block">
              <h2>{t('Receipt')}</h2>
              <div className="stats-block">
                <div>
                  {t('SelfCheckIn.Issue')} : {dayjs().format('YYYY/MM/DD')}{' '}
                </div>
                <div>
                  {t('SelfCheckIn.Reg. no.')} : {printCheckin.hotel.receiptRegistrationNumber}
                </div>
              </div>
              <div className="detail-block">
                <div className="list-item-block">
                  <div className="label">{t('address')}</div>
                  <div>
                    {props.state.payment!.receiptName || printCheckin.guestName}
                    <span style={{ paddingLeft: 16 }}>{t('SelfCheckIn.To')}</span>
                  </div>
                </div>
                <hr className="-normal" />
                <div className="list-item-block">
                  <div className="label">{t('Amount of money')}</div>
                  <div>¥{props.state.payment!.paymentAmount.toLocaleString()}</div>
                </div>
                <hr className="-normal" />
                <div className="breakdown-block">
                  <div>
                    {t('SelfCheckIn.For 10%')}¥{props.state.payment!.totalAmountAt10Per.toLocaleString()}({t('Tax')}¥
                    {props.state.payment!.totalTaxAt10Per.toLocaleString()})
                  </div>
                  <div>
                    {t('SelfCheckIn.For 8%')}¥{props.state.payment!.totalAmountAt8Per.toLocaleString()}({t('Tax')}¥
                    {props.state.payment!.totalTaxAt8Per.toLocaleString()})
                  </div>
                </div>
              </div>

              <div className="note-text">{receiptProviso}</div>
              {printCheckin.hotel.receiptTaxOfficeName && (
                <div className="stamp-tax-declaration-box">
                  <p>印紙税申告納</p>
                  <p>付につき{printCheckin.hotel.receiptTaxOfficeName}</p>
                  <p>税務署承認済</p>
                </div>
              )}
            </div>
            <hr className="-block" />
            {props.state.payment?.salesSlip?.type === 'credit' && <TerminalCreditSalesSlip receipt={props.state.payment.salesSlip} />}
            {props.state.payment?.salesSlip?.type === 'qr' && <QrCodeSalesSlip receipt={props.state.payment.salesSlip} />}
          </>
        )}

        {hasUnpaid && (
          <>
            <div className="payment-front-block">
              <h2>{t('SelfCheckIn.Details of use')}</h2>
              <div className="list-item-block">
                <div className="list-item">
                  <span className="label">{t('SelfCheckinPayment.AmountUsed')}</span>
                  <span className="value">¥{props.state.confirmUsageTotalAmount.toLocaleString()}</span>
                </div>
                <div className="list-item">
                  <span className="label">{t('SelfCheckinPayment.PaymentOption')}</span>
                  <span className="value">{t('SelfCheckinPayment.PayAtTheFrontDesk')}</span>
                </div>
              </div>
            </div>
            <hr className="-block" />
          </>
        )}

        <div className="guest-app-block">
          <div>
            <div className="title">
              <Trans i18nKey={t('SelfCheckIn.Information on being in the country can be found on your smartphone')} />
            </div>
            <hr className="-normal" style={{ margin: '12px 0' }} />
            <div className="icon-block">
              <div className="help-icon">
                <img src={require('@/static/images/icon_checkout_black.svg')} />
                <span>{t('SelfCheckIn.Facility')}</span>
              </div>
              <div className="help-icon">
                <img src={require('@/static/images/icon_map_black.svg')} />
                <span>{t('SelfCheckIn.Travel')}</span>
              </div>
              <div className="help-icon">
                <img src={require('@/static/images/icon_wifi_black.png')} />
                <span>Wi−Fi</span>
              </div>
            </div>
          </div>
          <QRCode value={printCheckin.guestAppUrl} size={128} />
        </div>

        <div className="hotel-block">
          <div style={{ paddingBottom: 8 }}>
            <span>{pickByLanguage(printCheckin.hotel.name)}</span>
          </div>
          {printCheckin.hotel.postalCode && (
            <div>
              <span>〒{printCheckin.hotel.postalCode}</span>
            </div>
          )}
          <div>
            <span>{pickByLanguage(printCheckin.hotel.address)}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
const receiptStyle = css({
  display: 'none',
  fontFamily: 'Noto Sans JP',
  lineHeight: 1.5,
  '.logo-block': {
    textAlign: 'center',
    paddingBottom: 30,
    img: {
      width: 400,
      height: 150,
    },
  },
  '.accommodation-block': {
    padding: '0 64px',
    hr: { margin: '48px 0 !important' },
    '.room-block': {
      '>div': {
        display: 'flex',
        alignItems: 'center',
        color: '#272727',
        lineHeight: 1,
        paddingTop: 32,
        '&:first-of-type': { paddingTop: 0 },
        '.label': {
          width: 154,
          minWidth: 154,
          fontSize: 28,
          letterSpacing: 1.4,
        },
        '.value': {
          paddingLeft: 48,
          fontSize: 42,
          letterSpacing: 2.1,
        },
      },
      // 宿泊期間表示用のスタイル（部屋番号より小さく）
      '.stay-period-item': {
        '.stay-period-value': {
          fontSize: 34,
          letterSpacing: 1.2,
        },
      },
      '.qr-code-container': {
        display: 'flex',
        flexDirection: 'column',
        gap: 32,
      },
      '.qr-code-room-number': {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
      },
      '.qr-code-block': {
        textAlign: 'center',
        img: {
          width: 160,
          height: 160,
        },
      },
      '.key-number-block': {
        textAlign: 'center',
        fontSize: 24,
        border: '1px solid #272727',
        width: '100%',
        paddingTop: 12,
        paddingBottom: 12,
      },
      '.qrcode-description': {
        fontSize: 22,
        color: '#272727',
        lineHeight: 1.4,
      },
    },
    '.reservation-block': {
      '.list-item': {
        '.label': {
          width: 120,
          minWidth: 120,
        },
      },
    },
    '.comment': {
      fontSize: 24,
      whiteSpace: 'break-spaces',
      color: '#272727',
    },
  },
  '.payment-card-block': {
    padding: '0 64px',
    color: '#272727',
    letterSpacing: 1.2,
    fontSize: 24,
    '.stats-block': { paddingTop: 24 },
    '.detail-block': {
      paddingTop: 48,
      '.list-item-block': {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: 28,
        '.label': { width: 100, minWidth: 100 },
      },
    },
    '.breakdown-block': { textAlign: 'right' },
    '.note-text': { paddingTop: 48 },
    '.stamp-tax-declaration-box': {
      width: 220,
      heigth: 160,
      border: '4px solid #272727',
      padding: '0px 4px',
      margin: '32px auto 0',
      fontSize: 22,
      lineHeight: 2.2,
      p: {
        width: '100%',
        whiteSpace: 'nowrap',
        textAlign: 'justify',
        textAlignLast: 'justify',
      },
    },
  },
  '.payment-front-block': {
    padding: '0 64px',
    '.list-item-block': { paddingTop: 48 },
  },
  '.guest-app-block': {
    padding: '0 64px',
    display: 'flex',
    gap: 32,
    alignItems: 'center',
    '>div:first-of-type': { flexGrow: 1 },
    '.title': {
      textAlign: 'center',
      fontSize: 24,
      letterSpacing: 1.6,
      color: '#272727',
    },
    '.icon-block': {
      display: 'flex',
      gap: 24,
      justifyContent: 'center',
      marginTop: 32,
      '.help-icon': {
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'left',
        fontSize: 18,
        letterSpacing: 1.2,
        color: '#272727',
        alignItems: 'center',
        width: 86,
        'svg, img': { height: 36, width: 36 },
        span: { paddingTop: 8, fontSize: 18 },
      },
    },
  },
  '.hotel-block': {
    padding: '24px 64px',
    div: {
      fontSize: 24,
      letterSpacing: 1.2,
      color: '#272727',
    },
  },

  '.list-item': {
    display: 'flex',
    alignItems: 'top',
    color: '#272727',
    lineHeight: 1.2,
    paddingTop: 24,
    '&:first-of-type': { paddingTop: 0 },
    '.label': {
      width: 154,
      minWidth: 154,
      fontSize: 24,
      letterSpacing: 1.2,
    },
    '.value': {
      '::before': { content: '":"', paddingRight: 8 },
      fontSize: 24,
      letterSpacing: 1.2,
      textIndent: '-1em',
      paddingLeft: '1em',
    },
  },
  h2: {
    textAlign: 'center',
    fontSize: 32,
  },
  hr: {
    '&.-normal': { borderTop: '4px solid #272727' },
    '&.-block': {
      borderTop: '4px dashed #A3A3A3',
      margin: '40px 0',
    },
  },
})
